import { useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router";
import { getBatchProgress } from "../lib/api";
import {
  describeProgress,
  mediaToastId,
  subscribeTracked,
  trackedSnapshot,
  untrackMedia,
} from "../lib/media-tracker";
import { dismissToast, toast } from "./ui/toast";

/** Takt odpytywania. Etapy zmieniają się co kilka sekund, procent — co sekundę. */
const TICK_MS = 1500;
/** Jak długo zostaje komunikat końcowy — „Oglądaj" ma dać się kliknąć. */
const FINAL_MS = 12_000;

/**
 * Prowadzi powiadomienie każdego śledzonego odcinka przez kolejne etapy.
 *
 * Jedno żądanie na takt dla wszystkich śledzonych naraz — ta sama trasa, z której
 * korzysta lista odcinków. Odpytujemy wyłącznie, gdy jest kogo śledzić.
 *
 * **Na stronie odtwarzacza tego materiału powiadomienia nie odświeżamy**: tam te
 * same etapy stoją na pełnym ekranie, a dubel w rogu tylko by go zasłaniał.
 * Komunikat z chwili kliknięcia dobiega wtedy swojego czasu i znika; wraca, gdy
 * widz wyjdzie z odtwarzacza przed końcem.
 */
export function MediaTracker() {
  const items = useSyncExternalStore(subscribeTracked, trackedSnapshot, trackedSnapshot);
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const key = items.map((m) => m.mediaFileId).join(",");

  useEffect(() => {
    if (key === "") return;
    const ids = key.split(",");
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      try {
        const data = await getBatchProgress(ids);
        if (cancelled) return;

        for (const item of data.items) {
          const media = trackedSnapshot().find((m) => m.mediaFileId === item.mediaFileId);
          if (media === undefined) continue;
          const toastId = media.toastId ?? mediaToastId(media.mediaFileId);
          const message = describeProgress(item);
          const watching = pathRef.current === `/odtwarzacz/${item.mediaFileId}`;

          if (message.final) untrackMedia(item.mediaFileId);
          if (watching) {
            // Gotowy przy otwartym odtwarzaczu: obraz właśnie rusza, komunikat
            // „Gotowy — Oglądaj" byłby wezwaniem do czegoś, co już się dzieje.
            if (message.final) dismissToast(toastId);
            continue;
          }

          toast({
            id: toastId,
            title: media.label,
            description: message.description,
            tone: message.tone,
            ...(message.progress !== undefined ? { progress: message.progress } : {}),
            ...(message.final ? { durationMs: FINAL_MS } : {}),
            ...(item.state === "ready"
              ? { action: { label: "Oglądaj", onClick: () => navigate(media.href) } }
              : item.state === "failed"
                ? {
                    // Do odtwarzacza, bo tam mieszka tabela prób i ponowienie.
                    action: {
                      label: "Szczegóły",
                      onClick: () => navigate(`/odtwarzacz/${item.mediaFileId}`),
                    },
                  }
                : {}),
            // Zamknięcie w trakcie to „nie chcę o tym słyszeć", nie „przerwij":
            // pobieranie idzie dalej, przestajemy tylko o nim mówić.
            onDismiss: () => untrackMedia(item.mediaFileId),
          });
        }

        // Materiał usunięty w międzyczasie (lista plików, „Przerwij") znika z odpowiedzi.
        for (const id of ids) {
          if (data.items.some((i) => i.mediaFileId === id)) continue;
          const media = trackedSnapshot().find((m) => m.mediaFileId === id);
          untrackMedia(id);
          dismissToast(media?.toastId ?? mediaToastId(id));
        }
      } catch {
        // Pojedyncza nieudana próba nie jest awarią — kolejny takt ją powtórzy.
      }
      if (!cancelled) timer = setTimeout(() => void tick(), TICK_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [key, navigate]);

  return null;
}
